/**
 * PATH: packages/ui/src/kanban/components/card-detail/operatorVisibleSummary.ts
 * FILE_ID: UI-KANBAN-OPERATOR-VISIBLE-SUMMARY-001
 * PURPOSE: Decide what the Card-tab Description editor should show when
 *          Card.summary was auto-filled with an email body.
 * WHY:     Auto-create used to copy ai_summary / body_preview into summary.
 *          Trello import also stored the full message in description. When
 *          the card already has linked emails, that dump is the email, not
 *          operator notes. Hide it in the editor without PATCHing empty.
 * ROLE IN ARCHITECTURE: Pure I→O helper for the card drawer edit baseline.
 * MAIN EXPORTS:
 *   - cardHasLinkedEmails()
 *   - looksLikeEmailDump()
 *   - operatorVisibleSummary()
 * NON-RESPONSIBILITIES: Does not write cards or detach emails.
 * NOTES FOR FUTURE AI: Compare blur/flush against this visible baseline,
 *   never raw card.summary, or a hidden dump will wipe on first blur.
 */

import { orderLinkedEmailIds } from './linkedEmails'

const EMAIL_DUMP_MARKERS: RegExp[] = [
  /view in gmail/i,
  /-----original message-----/i,
  /begin forwarded message/i,
  /^from:\s+.+\@.+/im,
  /<html[\s>]/i,
  /content-type:\s*multipart/i,
]

export function cardHasLinkedEmails(card: {
  primary_email?: string | null
  related_emails?: readonly string[] | null
}): boolean {
  return orderLinkedEmailIds(card.primary_email, card.related_emails).length > 0
}

export function looksLikeEmailDump(text: string): boolean {
  const value = text.trim()
  if (!value) return false
  return EMAIL_DUMP_MARKERS.some((re) => re.test(value))
}

export function operatorVisibleSummary(card: {
  summary?: string | null
  primary_email?: string | null
  related_emails?: readonly string[] | null
}): string {
  const raw = card.summary ?? ''
  if (!cardHasLinkedEmails(card)) return raw
  if (looksLikeEmailDump(raw)) return ''
  return raw
}
